"use client";
import React, { useRef } from "react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import { useGSAP } from "@gsap/react";
import { SplitText } from "gsap/all";

gsap.registerPlugin(Flip, SplitText);
interface LoaderProps {
  onComplete?: () => void; // callback dari parent
}
const Loader: React.FC<LoaderProps> = ({ onComplete }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      const wrap = document.querySelector(".wrap");
      const left = document.querySelector(".left");
      const right = document.querySelector(".right");
      const mask = document.querySelector("#revealMask");
      if (!wrap || !left || !mask || !right) return;
      wrap.classList.remove("!items-center", "!flex-col", "!justify-center", "!gap-4");
      wrap.classList.add("items-end");
      // === FLIP PERTAMA ===
      const firstFlip = Flip.getState(".wrap > div");
      wrap.classList.add("!items-center");
      wrap.classList.remove("items-end");
      // === ANIMASI FLIP PERTAMA ===
      Flip.from(firstFlip, {
        delay: 2.5,
        duration: 1,
        ease: "power2.inOut",
        scale: true,
        fade: true,
        onComplete: () => {
          const images = gsap.utils.toArray(".wrap > div") as HTMLElement[];

          // sembunyikan semua gambar dulu
          gsap.set(images, { autoAlpha: 0 });
          gsap.set(images[0], { autoAlpha: 1 });

          // === TIMELINE SLIDESHOW ===
          const tl = gsap.timeline({
            onComplete: () => {
              // === FLIP KEDUA ===
              const flipState = Flip.getState(".wrap > div");

              wrap.classList.remove("!items-center");
              wrap.classList.add("!flex-col", "!items-center", "!justify-center", "!gap-4");
              // ubah semua jadi relative layout
              images.forEach((img) => {
                img.classList.remove("absolute");
                img.classList.add("relative", "!w-20", "!h-20");
                gsap.set(img, { autoAlpha: 1 });
              });

              // FLIP layout transition
              Flip.from(flipState, {
                duration: 1.2,
                ease: "power2.inOut",
                stagger: 0.05,
                scale: true,
                fade: true,
                onComplete: () => {
                  // === FINAL: fade out dari bawah ke atas ===
                  gsap.to(images.slice().reverse(), {
                    autoAlpha: 0,
                    duration: 0.6,
                    ease: "power1.inOut",
                    stagger: 0.15,
                    onComplete: () => {
                      const final = Flip.getState(".left, .rigth, .left > *, .right > *");

                      left.children[1]?.classList.toggle("hidden");
                      right.children[1]?.classList.toggle("hidden");

                      Flip.from(final, {
                        duration: 1.2,
                        ease: "power2.inOut",
                        absolute: true,
                        nested: true,
                        onEnter: (self) => {
                          gsap.from(self, { autoAlpha: 0 });
                        },
                        onLeave: (self) => {
                          gsap.to(self, { autoAlpha: 0 });
                        },
                        // scale: true,
                        // fade: true,
                        onComplete: () => {
                          gsap.to([".left", ".right", ".wrap"], {
                            autoAlpha: 0,
                            duration: 0.8,
                            ease: "power2.inOut",
                            onComplete: () => {
                              document.querySelectorAll(".left, .right, .wrap").forEach((el) => el.remove());
                              gsap.fromTo(
                                mask,
                                { clipPath: "circle(5% at 50% 50%)" },
                                {
                                  clipPath: "circle(150% at 50% 50%)",
                                  duration: 0.5,
                                  ease: "power4.inOut",
                                  onComplete: () => {
                                    // hilangkan mask agar halaman utama terlihat
                                    gsap.to(mask, {
                                      autoAlpha: 1,
                                      duration: 0.6,
                                      ease: "power4.inOut",

                                      onComplete: () => {
                                        onComplete?.(); // panggil callback parent
                                      }
                                    });
                                  }
                                }
                              );
                            }
                          });
                        }
                      });
                    }
                  });
                }
              });
            }
          });

          // slideshow fade antar gambar
          images.forEach((img, i) => {
            const next = images[(i + 1) % images.length];
            tl.to(img, {
              autoAlpha: 0,
              duration: 0.1,
              ease: "power1.inOut"
            }).to(
              next,
              {
                autoAlpha: 1,
                duration: 0.3,
                ease: "power1.inOut"
              },
              "<+0.2"
            );
          });
        }
      });

      // animasi teks di kiri/kanan
      const tl1 = gsap.timeline({
        delay: 1.85,
        defaults: {
          opacity: 0
        }
      });
      document.fonts.ready.then(() => {
        const textSplit = new SplitText([".foot"], {
          type: "lines,words",
          linesClass: "line",
          mask: "lines"
        });
        tl1
          .from(".left", {
            y: -100,
            opacity: 0,
            duration: 1,
            ease: "power2.out"
          })
          .to(".left", {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power1.inOut"
          })
          .fromTo(
            ".right",
            {
              y: 100,
              opacity: 0,
              ease: "power1.inOut"
            },
            {
              y: 0,
              opacity: 1,
              ease: "power1.inOut"
            },
            "-=1.5"
          )
          .from(
            textSplit.lines,
            {
              yPercent: 100,
              opacity: 0,
              duration: 0.6,
              stagger: 0.1,
              ease: "expo.inOut"
            },
            "-=0.5" // mulai sedikit overlap dengan animasi kanan
          )
          .to(textSplit.lines, {
            transform: "rotate3d(1, 0, 0, 90deg)",
            duration: 1.5,
            delay: 2,
            ease: "power4.in"
          });
      });
    },
    { scope: containerRef }
  );

  return (
    <div ref={containerRef} className='min-h-screen  flex flex-col w-full bg-beige-300 text-white overflow-hidden'>
      <div id='revealMask' className='fixed inset-0 bg-beige-900   z-[9999] pointer-events-none' style={{ clipPath: "circle(0% at 50% 50%)" }}></div>
      <div
        className='flex flex-row flex-1 gap-2'
        style={{
          WebkitMaskImage: "radial-gradient(circle at 50% 50%, transparent 0%, black 0%)",
          maskImage: "radial-gradient(circle at 50% 50%, transparent 0%, black 0%)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%"
        }}
      >
        {/* LEFT TEXT */}
        <div className='w-96 flex items-center justify-center left text-center text-xs md:text-3xl text-neutral-800 uppercase font-bold gap-2 pl-7 flex-col md:flex-row'>
          <span className='flex'>Capture</span> <span className='hidden text-white font-surgena text-base md:text-3xl'>PHOTO</span>
          <span className=''>Moments Forever.</span>
        </div>

        {/* FLIP AREA */}
        <div className='flex-1 flex items-end justify-center relative wrap'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='absolute md:w-60 md:h-60 size-15 bg-cover bg-center' style={{ backgroundImage: `url('/image/wiko_studio/seq${i + 1}.png')` }} />
          ))}
        </div>

        {/* RIGHT TEXT */}
        <div className='w-96 flex items-center justify-center right gap-2 text-xs md:text-5xl pr-7 font-bold font-surgena uppercase text-center flex-col md:flex-row '>
          <span className=''>Beyond</span>
          <span className='hidden text-black font-surgena text-base md:text-3xl'>STUDIO</span>
          <span className=''>The Lens</span>
        </div>
      </div>

      <div className='flex items-center justify-center text-center p-6 '>
        <h1 className='foot max-w-xl font-bebas-neue perspective-dramatic transform-3d text-beige-900'>ipsum dolor sit amet consectetur adipisicing elit. Error quos nisi dolorem, impedit dolor mollitia! Possimus ea ut sunt fugit?</h1>
      </div>
    </div>
  );
};

export default Loader;
